
import { useState } from 'react';
import useScrollAnimation from '../../../hooks/useScrollAnimation';

const FaqSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation({ threshold: 0.2 });

  const faqs = [
    {
      pregunta: '¿Qué tipo de vehículos compráis en Gavá?',
      respuesta: 'Compramos todo tipo de vehículos en Gavá: coches siniestrados, averiados, con problemas mecánicos graves, vehículos antiguos o para desguace. Aceptamos todas las marcas y modelos, independientemente de su estado.',
    },
    {
      pregunta: '¿Cuánto tardáis en recoger el coche en Gavá?',
      respuesta: 'Normalmente recogemos el vehículo en 24-48 horas desde que aceptas nuestra oferta. En casos urgentes, podemos acelerar el proceso y recogerlo el mismo día en Gavá y alrededores.',
    },
    {
      pregunta: '¿Tengo que llevar el coche al desguace de Gavá?',
      respuesta: 'No, nosotros nos desplazamos a donde esté tu vehículo en Gavá, Castelldefels, Viladecans o cualquier localidad de la comarca. El servicio de recogida con grúa está incluido sin coste adicional.',
    },
    {
      pregunta: '¿Cómo se realiza el pago por mi coche?',
      respuesta: 'El pago se realiza en el momento de la recogida del vehículo. Puedes elegir entre efectivo o transferencia bancaria instantánea. No hay esperas ni trámites complicados.',
    },
    {
      pregunta: '¿Qué documentación necesito para vender mi coche en Gavá?',
      respuesta: 'Necesitas el DNI del titular, el permiso de circulación y la ficha técnica del vehículo. Nosotros nos encargamos de todos los trámites de baja en tráfico y certificado de destrucción.',
    },
    {
      pregunta: '¿Compráis coches sin ITV o con multas pendientes?',
      respuesta: 'Sí, compramos vehículos sin ITV en vigor en Gavá. En cuanto a multas pendientes, es importante que estén saldadas antes de la venta, aunque podemos asesorarte sobre cómo gestionarlas.',
    },
  ];

  const toggleFaq = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-24 px-6 lg:px-12 bg-white">
      <div className="max-w-4xl mx-auto">
        <div
          ref={headerRef}
          className={`text-center mb-16 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Preguntas Frecuentes sobre Desguace en Gavá
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Resolvemos tus dudas sobre la venta de coches siniestrados y averiados en Gavá
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <FaqItem key={index} faq={faq} index={index} isOpen={openIndex === index} onToggle={() => toggleFaq(index)} />
          ))}
        </div>

        <div
          ref={ctaRef}
          className={`mt-12 text-center bg-emerald-50 rounded-2xl p-8 transition-all duration-700 ${
            ctaVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          }`}
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            ¿Tienes más preguntas sobre nuestro desguace en Gavá?
          </h3>
          <p className="text-gray-600 mb-6">
            Llámanos o escríbenos por WhatsApp y te atendemos de inmediato
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:932932913"
              className="inline-flex items-center space-x-2 bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 rounded-lg font-semibold transition-all hover:scale-105 whitespace-nowrap"
            >
              <i className="ri-phone-fill"></i>
              <span>932 932 913</span>
            </a>
            <a
              href="https://wa.me/34932932913"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center space-x-2 bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 rounded-lg font-semibold transition-all hover:scale-105 whitespace-nowrap"
            >
              <i className="ri-whatsapp-fill"></i>
              <span>WhatsApp</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

const FaqItem = ({ faq, index, isOpen, onToggle }: { faq: { pregunta: string; respuesta: string }; index: number; isOpen: boolean; onToggle: () => void }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <div
      ref={ref}
      className={`bg-gray-50 rounded-xl overflow-hidden border border-gray-200 hover:border-emerald-600 transition-all duration-500 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <button
        onClick={onToggle}
        className="w-full px-8 py-6 flex items-center justify-between text-left cursor-pointer"
      >
        <h4 className="text-lg font-bold text-gray-900 pr-4">
          <strong>{faq.pregunta}</strong>
        </h4>
        <i
          className={`${
            isOpen ? 'ri-subtract-line' : 'ri-add-line'
          } text-2xl text-emerald-600 transition-transform flex-shrink-0`}
        ></i>
      </button>
      {isOpen && (
        <div className="px-8 pb-6">
          <p className="text-gray-600 leading-relaxed">{faq.respuesta}</p>
        </div>
      )}
    </div>
  );
};

export default FaqSection;
